import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import Header from './components/Header';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import CartDrawer from './components/cart/CartDrawer';
import HomePage from './pages/HomePage';
import PartsPage from './pages/PartsPage';
import PartDetailPage from './pages/PartDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';

import ProfilePage from './pages/ProfilePage';
import FavoritesPage from './pages/FavoritesPage';
import PurchasesPage from './pages/PurchasesPage';
import PurchaseDetailPage from './pages/PurchaseDetailPage';
import MessagesPage from './pages/MessagesPage';
import NotificationsPage from './pages/NotificationsPage';
import MyShopPage from './pages/MyShopPage';


const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime:            5 * 60 * 1000,  // data stays fresh 5 min — no redundant refetches
      gcTime:              10 * 60 * 1000,  // keep unused cache 10 min
      refetchOnWindowFocus: false,           // never refetch just because user switched tabs
      refetchOnReconnect:   true,            // do refetch after network drops
      // Do not retry 4xx errors (except 408 timeout, 429 rate-limit handled by axios)
      retry: (failureCount, error) => {
        const status = error?.response?.status ?? error?.status;
        if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) return false;
        return failureCount < 2;
      },
    },
  },
});



const AppRoutes = () => {
  return (
    <Router>
      <CartDrawer />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Header />
            <Navigation />
            <Box component="main" sx={{ flexGrow: 1 }}>
              <HomePage />
            </Box>
            <Footer />
          </Box>
        } />

        {/* Parts Routes */}
        <Route path="/parts" element={
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Header />
            <Box component="main" sx={{ flexGrow: 1 }}>
              <PartsPage />
            </Box>
            <Footer />
          </Box>
        } />
        <Route path="/parts/:partNumber" element={
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Header />
            <Box component="main" sx={{ flexGrow: 1 }}>
              <PartDetailPage />
            </Box>
            <Footer />
          </Box>
        } />
        <Route path="/parts/:id/:partNumber" element={
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Header />
            <Box component="main" sx={{ flexGrow: 1 }}>
              <PartDetailPage />
            </Box>
            <Footer />
          </Box>
        } />

        {/* Cart & Checkout Routes */}
        <Route path="/cart" element={
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Header />
            <Box component="main" sx={{ flexGrow: 1 }}>
              <CartPage />
            </Box>
            <Footer />
          </Box>
        } />
        <Route path="/checkout" element={
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Header />
            <Box component="main" sx={{ flexGrow: 1 }}>
              <CheckoutPage />
            </Box>
            <Footer />
          </Box>
        } />

        {/* Auth Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        {/* User Routes */}
        <Route path="/profile" element={
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Header />
            <Box component="main" sx={{ flexGrow: 1 }}>
              <ProfilePage />
            </Box>
            <Footer />
          </Box>
        } />
        <Route path="/favorites" element={
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Header />
            <Box component="main" sx={{ flexGrow: 1 }}>
              <FavoritesPage />
            </Box>
            <Footer />
          </Box>
        } />
        <Route path="/orders" element={
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Header />
            <Box component="main" sx={{ flexGrow: 1 }}>
              <PurchasesPage />
            </Box>
            <Footer />
          </Box>
        } />

        {/* Marketplace Routes — /listings redirects to /parts */}
        <Route path="/listings" element={<Navigate to="/parts" replace />} />
        <Route path="/listings/:id" element={<Navigate to="/parts" replace />} />

        {/* My Listings — Create & Manage own listings */}
        {/* My Shop */}
        <Route path="/my-shop" element={
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Header />
            <Box component="main" sx={{ flexGrow: 1 }}><MyShopPage /></Box>
            <Footer />
          </Box>
        } />

        {/* My Listings — alias redirects to My Shop */}
        <Route path="/my-listings" element={<Navigate to="/my-shop" replace />} />

        {/* Messages Route */}
        <Route path="/messages" element={
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Header />
            <Box component="main" sx={{ flexGrow: 1 }}>
              <MessagesPage />
            </Box>
            <Footer />
          </Box>
        } />

        {/* Notifications */}
        <Route path="/notifications" element={
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Header />
            <Box component="main" sx={{ flexGrow: 1 }}><NotificationsPage /></Box>
            <Footer />
          </Box>
        } />

        {/* Order Detail */}
        <Route path="/orders/:orderId" element={
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Header />
            <Box component="main" sx={{ flexGrow: 1 }}><PurchaseDetailPage /></Box>
            <Footer />
          </Box>
        } />


      </Routes>
    </Router>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <CartProvider>
            <AppRoutes />
          </CartProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
